/**
 * What an edit adds and removes (prototype diffLines), for the "Bijbetalen" and "Terug te geven"
 * windows: "Bijbesteld: 2× Coca-Cola 33 cl", "Geschrapt: Margherita Medium". Lines count as the same
 * when product, size, options, note and "gratis" are the same.
 */
import type { PricedLine } from "../../api/types";
import { describeModifier } from "./modifiers";

type Line = Pick<PricedLine, "name" | "variant" | "quantity" | "modifiers" | "note" | "free">;

const contentOf = (line: Line) =>
  JSON.stringify([
    line.name,
    line.variant,
    line.modifiers.map((modifier) => describeModifier(modifier, false)),
    line.note,
    line.free,
  ]);

const nameOf = (line: Line) => (line.variant === null ? line.name : `${line.name} ${line.variant}`);

export function lineChanges(before: readonly Line[], after: readonly Line[]): { added: string[]; removed: string[] } {
  const count = (lines: readonly Line[]) => {
    const units = new Map<string, { line: Line; quantity: number }>();
    for (const line of lines) {
      const key = contentOf(line);
      const current = units.get(key) ?? { line, quantity: 0 };
      current.quantity += line.quantity;
      units.set(key, current);
    }
    return units;
  };
  const was = count(before);
  const is = count(after);
  const more = (from: typeof was, to: typeof was) =>
    [...to.entries()].flatMap(([key, { line, quantity }]) => {
      const extra = quantity - (from.get(key)?.quantity ?? 0);
      return extra > 0 ? [`${String(extra)}× ${nameOf(line)}`] : [];
    });
  return { added: more(was, is), removed: more(is, was) };
}
